import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container, 
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const HomePage: React.FC = () => {
  const { isAuthenticated, loading: authLoading, schedulingEnabled } = useAuth();
  const navigate = useNavigate();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
        <Typography variant="h6" ml={2}>Checking authentication status...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect to login page via useEffect
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Container component="main" sx={{ flexGrow: 1, py: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Court Reporter Dashboard
        </Typography>
        
        <Grid container spacing={3}>
          {/* Recent Jobs Card */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h5" component="div" gutterBottom>
                  Recent Depositions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and manage your recent depositions. Complete worksheets for recent jobs.
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => navigate('/jobs')}>
                  View Jobs
                </Button>
              </CardActions>
            </Card>
          </Grid>
          
          {/* Schedule Management */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h5" component="div" gutterBottom>
                  Manage Schedule
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Set your availability for virtual and in-person depositions. View and edit your weekly schedule.
                </Typography>
              </CardContent>
              <CardActions>
                {schedulingEnabled ? (
                  <Button size="small" onClick={() => navigate('/schedule')}>
                    Manage Schedule
                  </Button>
                ) : (
                  <Button size="small" disabled>
                    Coming Soon
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
          
          {/* Account Settings placeholder */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h5" component="div" gutterBottom>
                  Account Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage your account preferences and settings.
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => navigate('/account-settings')}>
                  Manage Settings
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* Resources */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h5" component="div" gutterBottom>
                  Resources
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Access helpful documents, guides, and templates.
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => navigate('/resources')}>
                  View Resources
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </Box>
  );
};

export default HomePage;